import * as XLSX from 'xlsx';
import { Pedido, Selection } from '../../types';

// Helper function to format currency for Excel (as number)
const formatCurrency = (value: number): number => {
    return Math.round(value);
};

// Helper function to format product name (like RenderSelections does)
const formatProductName = (selection: Selection): string => {
    let productName = `${selection.quantity}x ${selection.item_name}`;
    
    // Add selection groups items
    if (selection.selection_groups && selection.selection_groups.length > 0) {
        const groupItems = selection.selection_groups
            .flatMap(sg => sg.selected_items.map(sgi => sgi.item_name))
            .filter(name => name);
        
        if (groupItems.length > 0) {
            productName += ` (${groupItems.join(', ')})`;
        }
    }
    
    // Add modifiers
    if (selection.modifiers && selection.modifiers.length > 0) {
        const modifierNames = selection.modifiers.map(mod => mod.option_name).filter(name => name);
        if (modifierNames.length > 0) {
            if (selection.selection_groups && selection.selection_groups.length > 0) {
                productName += `, ${modifierNames.join(', ')}`;
            } else {
                productName += ` (${modifierNames.join(', ')})`;
            }
        }
    }
    
    // Add comment if exists
    if (selection.comment) {
        productName += ` - Nota: ${selection.comment}`;
    }
    
    return productName;
};

// Helper function to format pedido status
const getPedidoStatus = (pedido: Pedido): string => {
    if (pedido.cancelled) return 'Cancelado';
    if (pedido.closed) return 'Pagado';
    if (pedido.printed && !pedido.closed && !pedido.cancelled && !pedido.isDelivered && !pedido.isReady) return 'Impreso';
    if (!pedido.closed && !pedido.cancelled && !pedido.isDelivered && pedido.isReady) return 'Listo';
    if (!pedido.closed && !pedido.cancelled && pedido.isDelivered && pedido.isReady) return 'Entregado';
    return 'Creado';
};

// Transform pedidos data for "Pedidos" sheet
const transformPedidosData = (pedidos: Pedido[]) => {
    return pedidos.map(pedido => {
        // Calculate total from selections
        const total = pedido.selections?.reduce((sum, sel) => sum + sel.total_price, 0) || 0;
        
        return {
            'ID Pedido': pedido.pedido_id || '',
            'Fecha': pedido.local_timestamp || pedido.timestamp || '',
            'Mesa': pedido.mesa_num || '',
            'Usuario': pedido.user_name || '',
            'Cliente': pedido.client_name || '',
            'Tipo Pedido': pedido.pedido_type_id || '',
            'App Externa': pedido.third_party_provider_name || '',
            'Estado': getPedidoStatus(pedido),
            'Total': formatCurrency(total),
            'Impreso': pedido.printed ? 'Sí' : 'No',
            'Listo': pedido.isReady ? 'Sí' : 'No',
            'Entregado': pedido.isDelivered ? 'Sí' : 'No',
            'Pagado': pedido.closed ? 'Sí' : 'No',
            'Cancelado': pedido.cancelled ? 'Sí' : 'No',
            'Manual': pedido.isManual ? 'Sí' : 'No',
            'Prioridad': pedido.priority || '',
            'Hora Entrega': pedido.hora_entrega || '',
            'Comentario': pedido.comment || '',
            'ID Recibo': pedido.receipt_id || '',
            'Sesión ID': pedido.session_id || '',
            'ID Turno': pedido.shift_id || '',
            'Turno': pedido.shift_name || 'N/A',
        };
    });
};

// Transform selections data for "Por Producto" sheet
const transformSelectionsData = (pedidos: Pedido[]) => {
    const selectionsData: any[] = [];
    
    pedidos.forEach(pedido => {
        if (!pedido.selections || pedido.selections.length === 0) return;
        
        pedido.selections.forEach((selection: Selection) => {
            selectionsData.push({
                'ID Pedido': pedido.pedido_id || '',
                'Producto': formatProductName(selection),
                'ID Producto': selection.item_id,
                'Cantidad': selection.quantity,
                'Precio Base': formatCurrency(selection.base_price),
                'Precio Total': formatCurrency(selection.total_price),
                'Usuario': pedido.user_name || '',
                'Type ID': pedido.pedido_type_id || '',
                'App Externa': pedido.third_party_provider_name || '',
                'Fecha': pedido.local_timestamp || pedido.timestamp || '',
                'Mesa': pedido.mesa_num || '',
                'Cliente': pedido.client_name || '',
                'Estado Pedido': getPedidoStatus(pedido),
                'ID Recibo': pedido.receipt_id || '',
                'Sesión ID': pedido.session_id || '',
                'ID Turno': pedido.shift_id || '',
                'Turno': pedido.shift_name || 'N/A',
                'Comentario Producto': selection.comment || '',
            });
        });
    });
    
    return selectionsData;
};

// Main export function
export const exportPedidosToExcel = (
    pedidos: Pedido[],
    fileName?: string
) => {
    try {
        // Transform data for both sheets
        const pedidosData = transformPedidosData(pedidos);
        const selectionsData = transformSelectionsData(pedidos);
        
        // Create workbook
        const wb = XLSX.utils.book_new();
        
        // Create "Pedidos" sheet
        const wsPedidos = XLSX.utils.json_to_sheet(pedidosData);
        
        // Set column widths for Pedidos sheet
        const pedidosColWidths = [
            { wch: 10 },  // ID Pedido
            { wch: 20 },  // Fecha
            { wch: 10 },  // Mesa
            { wch: 20 },  // Usuario
            { wch: 20 },  // Cliente
            { wch: 12 },  // Tipo Pedido
            { wch: 20 },  // App Externa
            { wch: 12 },  // Estado
            { wch: 12 },  // Total
            { wch: 10 },  // Impreso
            { wch: 10 },  // Listo
            { wch: 10 },  // Entregado
            { wch: 10 },  // Pagado
            { wch: 10 },  // Cancelado
            { wch: 10 },  // Manual
            { wch: 12 },  // Prioridad
            { wch: 20 },  // Hora Entrega
            { wch: 30 },  // Comentario
            { wch: 12 },  // ID Recibo
            { wch: 12 },  // Sesión ID
            { wch: 12 },  // ID Turno
            { wch: 15 },  // Turno
        ];
        wsPedidos['!cols'] = pedidosColWidths;
        
        // Add Pedidos sheet to workbook
        XLSX.utils.book_append_sheet(wb, wsPedidos, 'Pedidos');
        
        // Create "Por Producto" sheet
        const wsSelections = XLSX.utils.json_to_sheet(selectionsData);
        
        // Set column widths for Selections sheet
        const selectionsColWidths = [
            { wch: 10 },  // ID Pedido
            { wch: 50 },  // Producto (wider for full description)
            { wch: 12 },  // ID Producto
            { wch: 10 },  // Cantidad
            { wch: 12 },  // Precio Base
            { wch: 12 },  // Precio Total
            { wch: 20 },  // Usuario
            { wch: 10 },  // Type ID
            { wch: 20 },  // App Externa
            { wch: 20 },  // Fecha
            { wch: 10 },  // Mesa
            { wch: 20 },  // Cliente
            { wch: 12 },  // Estado Pedido
            { wch: 12 },  // ID Recibo
            { wch: 12 },  // Sesión ID
            { wch: 12 },  // ID Turno
            { wch: 15 },  // Turno
            { wch: 30 },  // Comentario Producto
        ];
        wsSelections['!cols'] = selectionsColWidths;
        
        // Add Selections sheet to workbook
        XLSX.utils.book_append_sheet(wb, wsSelections, 'Por Producto');
        
        // Generate filename
        const timestamp = new Date().toISOString().split('T')[0];
        const finalFileName = fileName || `Pedidos_${timestamp}.xlsx`;
        
        // Write file
        XLSX.writeFile(wb, finalFileName);
        
    } catch (error) {
        console.error('Error generating Excel file:', error);
        throw error;
    }
};

// Export filtered pedidos by date range
export const exportFilteredPedidos = (
    pedidos: Pedido[],
    startDate?: string,
    endDate?: string,
    fileName?: string
) => {
    let filteredPedidos = pedidos;
    
    // Apply date filters if provided
    if (startDate || endDate) {
        filteredPedidos = pedidos.filter(pedido => {
            const pedidoDate = pedido.local_timestamp || pedido.timestamp;
            if (!pedidoDate) return false;
            
            const date = new Date(pedidoDate);
            
            if (startDate && new Date(startDate) > date) return false;
            if (endDate && new Date(endDate) < date) return false;
            
            return true;
        });
    }
    
    const timestamp = new Date().toISOString().split('T')[0];
    const dateRange = startDate && endDate 
        ? `${startDate}_a_${endDate}`
        : timestamp;
    
    const finalFileName = fileName || `Pedidos_${dateRange}.xlsx`;
    
    exportPedidosToExcel(filteredPedidos, finalFileName);
};
